import React, { useState } from 'react';
import { TodoListContextType } from '../contexts/local/TodoListFactory';
import { useTheme } from '../contexts/global/ThemeContext';

// This component demonstrates LOCAL context usage
// It receives a specific todo list context and operates only within that scope

interface TodoListProps {
  useTodoList: () => TodoListContextType; // Hook from the factory
}

export function TodoList({ useTodoList }: TodoListProps) {
  // LOCAL context - specific to this todo category
  const {
    category,
    filteredTodos,
    stats,
    filter,
    isLoading,
    addTodo,
    toggleTodo,
    deleteTodo,
    setFilter,
    clearCompleted
  } = useTodoList();

  // GLOBAL context - for styling
  const { theme } = useTheme();

  // LOCAL component state (not in context)
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [newTodoPriority, setNewTodoPriority] = useState<'low' | 'medium' | 'high'>('medium');

  const handleAddTodo = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTodoTitle.trim()) {
      addTodo(newTodoTitle.trim(), undefined, newTodoPriority);
      setNewTodoTitle('');
    }
  };

  const getPriorityColor = (priority: 'low' | 'medium' | 'high') => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
    }
  };

  const getPriorityEmoji = (priority: 'low' | 'medium' | 'high') => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
    }
  };

  return (
    <div className="todo-list">
      <div className="todo-header">
        <h2 className="category-title">
          <span 
            className="category-color" 
            style={{ backgroundColor: category.color }}
          ></span>
          {category.name} Todos
        </h2>
        <span className="context-indicator">
          📍 Uses LOCAL context
        </span>
      </div>

      {/* Stats - from LOCAL context */}
      <div className="stats">
        <div className="stat">
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat">
          <span className="stat-number">{stats.active}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat">
          <span className="stat-number">{stats.completed}</span>
          <span className="stat-label">Done</span>
        </div>
      </div>

      {/* Add todo form */}
      <form onSubmit={handleAddTodo} className="add-todo-form">
        <input
          type="text"
          value={newTodoTitle}
          onChange={(e) => setNewTodoTitle(e.target.value)}
          placeholder={`Add a new ${category.name.toLowerCase()} todo...`}
          className="todo-input"
        />
        <select 
          value={newTodoPriority}
          onChange={(e) => setNewTodoPriority(e.target.value as 'low' | 'medium' | 'high')}
          className="priority-select"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <button type="submit" className="add-btn">
          Add
        </button>
      </form>

      {/* Filter buttons */}
      <div className="filters">
        {(['all', 'active', 'completed'] as const).map(filterType => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className={`filter-btn ${filter === filterType ? 'active' : ''}`}
          >
            {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
          </button>
        ))}
        {stats.completed > 0 && (
          <button onClick={clearCompleted} className="clear-btn">
            Clear Completed
          </button>
        )}
      </div>

      {/* Todo items */}
      <div className="todos">
        {isLoading ? (
          <div className="loading">Loading {category.name.toLowerCase()} todos...</div>
        ) : filteredTodos.length === 0 ? (
          <div className="empty">
            No {filter === 'all' ? '' : filter + ' '}todos in {category.name.toLowerCase()}
          </div>
        ) : (
          filteredTodos.map(todo => (
            <div key={todo.id} className={`todo-item ${todo.completed ? 'completed' : ''}`}>
              <div className="todo-main">
                <button 
                  onClick={() => toggleTodo(todo.id)}
                  className="toggle-btn"
                >
                  {todo.completed ? '✅' : '⭕'}
                </button>
                <span className="todo-title">{todo.title}</span>
                <span className="priority-indicator">
                  {getPriorityEmoji(todo.priority)}
                </span>
              </div>
              <button 
                onClick={() => deleteTodo(todo.id)}
                className="delete-btn"
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>

      <div className="context-explanation">
        <p><strong>Local Context Examples:</strong></p>
        <ul>
          <li>📝 Category: {category.name} - isolated from other categories</li>
          <li>📊 Stats: {stats.total} total, {stats.active} active - specific to this list</li>
          <li>🔍 Filter: {filter} - doesn't affect other lists</li>
          <li>Each category has its own independent state</li>
        </ul>
      </div>

      <style>{`
        .todo-list {
          background: ${theme === 'dark' ? '#2d3748' : '#ffffff'};
          color: ${theme === 'dark' ? '#ffffff' : '#2d3748'};
          border: 1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'};
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .todo-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .category-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          font-size: 1.25rem;
        }

        .category-color {
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
        }

        .context-indicator {
          background: #7c3aed;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
        }

        .stats {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 1rem;
          background: ${theme === 'dark' ? '#1a202c' : '#f8fafc'};
          border-radius: 0.375rem;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .stat-number {
          font-size: 1.5rem;
          font-weight: bold;
          color: ${category.color};
        }

        .stat-label {
          font-size: 0.75rem;
          opacity: 0.7;
        }

        .add-todo-form {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .todo-input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid ${theme === 'dark' ? '#4a5568' : '#d1d5db'};
          border-radius: 0.375rem;
          background: ${theme === 'dark' ? '#374151' : '#ffffff'};
          color: ${theme === 'dark' ? '#ffffff' : '#374151'};
        }

        .priority-select {
          padding: 0.5rem;
          border: 1px solid ${theme === 'dark' ? '#4a5568' : '#d1d5db'};
          border-radius: 0.375rem;
          background: ${theme === 'dark' ? '#374151' : '#ffffff'};
          color: ${theme === 'dark' ? '#ffffff' : '#374151'};
        }

        .add-btn {
          padding: 0.5rem 1rem;
          background: ${category.color};
          color: white;
          border: none;
          border-radius: 0.375rem;
          cursor: pointer;
          font-weight: 500;
        }

        .add-btn:hover {
          opacity: 0.8;
        }

        .filters {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 0.375rem 0.75rem;
          border: 1px solid ${theme === 'dark' ? '#4a5568' : '#d1d5db'};
          background: ${theme === 'dark' ? '#374151' : '#ffffff'};
          color: ${theme === 'dark' ? '#ffffff' : '#374151'};
          border-radius: 0.375rem;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .filter-btn.active {
          background: ${category.color};
          color: white;
          border-color: ${category.color};
        }

        .filter-btn:hover {
          opacity: 0.8;
        }

        .clear-btn {
          padding: 0.375rem 0.75rem;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 0.375rem;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .clear-btn:hover {
          background: #b91c1c;
        }

        .todos {
          min-height: 100px;
        }

        .loading, .empty {
          text-align: center;
          padding: 2rem;
          opacity: 0.6;
          font-style: italic;
        }

        .todo-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          background: ${theme === 'dark' ? '#374151' : '#f9fafb'};
          border-radius: 0.375rem;
          transition: all 0.2s;
        }

        .todo-item:hover {
          background: ${theme === 'dark' ? '#4a5568' : '#f3f4f6'};
        }

        .todo-item.completed {
          opacity: 0.6;
        }

        .todo-item.completed .todo-title {
          text-decoration: line-through;
        }

        .todo-main {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
        }

        .toggle-btn {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
        }

        .todo-title {
          flex: 1;
          font-weight: 500;
        }

        .priority-indicator {
          font-size: 1rem;
        }

        .delete-btn {
          background: none;
          border: none;
          font-size: 1rem;
          cursor: pointer;
          opacity: 0.6;
          padding: 0.25rem;
        }

        .delete-btn:hover {
          opacity: 1;
        }

        .context-explanation {
          margin-top: 1rem;
          padding: 1rem;
          background: ${theme === 'dark' ? '#1a202c' : '#f8fafc'};
          border-radius: 0.5rem;
          font-size: 0.875rem;
        }

        .context-explanation ul {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        .context-explanation li {
          margin: 0.25rem 0;
        }
      `}</style>
    </div>
  );
}
